import React from 'react';
import {
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { useField } from 'formik';
import { Option } from '../../interfaces';
import { cx } from '../../helpers/global';
import InputLabel from './InputLabel';

interface Props {
  values: any;
  setFieldValue: (name: string, value: string) => void;
  name: string;
  label: string;
  options: Option[];
  placeholder: string | undefined;
  disabled?: boolean;
  hidden?: boolean;
  validateChange?: (value: string) => boolean | Promise<boolean>;
  onChange?: (value: string) => void;
  tooltip?: string;
  autoWidth?: boolean;
  renderSuffix?: () => React.ReactNode;
  isError?: boolean;
}

export default function SelectInput({
  values,
  setFieldValue,
  name,
  label,
  options,
  placeholder,
  disabled,
  hidden,
  validateChange,
  onChange,
  tooltip,
  autoWidth = true,
  renderSuffix,
  isError: isErrorProp,
}: Props) {
  const [, { error, touched }, { setTouched }] = useField(name);
  const isError = isErrorProp || (touched && !!error);
  const onBlur = React.useCallback(() => {
    setTouched(true);
  }, [setTouched]);
  return (
    <div className={cx('input', hidden && 'hidden', isError && 'error')}>
      <InputLabel name={name} label={label} tooltip={tooltip} />
      <Select
        IconComponent={renderSuffix ? () => null : undefined}
        renderSuffix={renderSuffix}
        value={values[name] ?? ''}
        onChange={async (event: SelectChangeEvent) => {
          const value = event.target.value as string;
          const isValid = !validateChange || (await validateChange(value));
          if (!isValid) return;
          setFieldValue(name, value);
          onChange?.(value);
        }}
        input={<OutlinedInput />}
        autoWidth={autoWidth}
        displayEmpty
        label={placeholder}
        disabled={disabled}
        onBlur={onBlur}>
        <MenuItem disabled value="">
          <em>{placeholder}</em>
        </MenuItem>
        {options.map(option => (
          <MenuItem value={option.value as string} key={String(option.value)}>
            {option.label ? option.label : option.value}
          </MenuItem>
        ))}
      </Select>
    </div>
  );
}
